import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Tag, Clock } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import LazyImage from '../components/LazyImage';
import NewsletterCapture from '../components/NewsletterCapture';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');

  useEffect(() => {
    fetch(`${API}/blog`)
      .then(res => res.json())
      .then(data => setPosts(data.posts || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const categories = ['All', ...new Set(posts.map(p => p.category))];
  const filtered = activeCategory === 'All' ? posts : posts.filter(p => p.category === activeCategory);
  const featured = filtered.find(p => p.featured);
  const regular = filtered.filter(p => !p.featured);

  const formatDate = (d) => new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
  const readTime = (content) => `${Math.max(1, Math.ceil(content.replace(/<[^>]*>/g, '').split(/\s+/).length / 200))} min read`;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#00AEEF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Hero */}
      <section className="bg-gradient-to-r from-[#003366] to-[#001a33] text-white py-20 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Pool Insights & Projects</h1>
          <p className="text-lg text-[#00AEEF]/80 max-w-2xl mx-auto">Expert tips, project showcases, and the latest in swimming pool technology from Aquarist Pools.</p>
        </div>
      </section>

      {/* Category Filter */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex flex-wrap gap-2 justify-center" data-testid="blog-category-filter">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                activeCategory === cat
                  ? 'bg-[#003366] text-white shadow-lg'
                  : 'bg-gray-100 text-gray-600 hover:bg-[#00AEEF]/10 hover:text-[#00AEEF]'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 pb-20">
        {/* Featured Post */}
        {featured && (
          <Link to={`/blog/${featured.slug}`} className="block mb-12 group" data-testid="featured-blog-post">
            <Card className="overflow-hidden border-0 shadow-xl hover:shadow-2xl transition-all duration-300">
              <div className="grid md:grid-cols-2 gap-0">
                <div className="relative h-64 md:h-auto md:min-h-[400px] overflow-hidden">
                  <LazyImage
                    src={featured.cover_image}
                    alt={featured.title}
                    className="w-full h-full"
                    zoomable={false}
                  />
                  <div className="absolute top-4 left-4 bg-[#00AEEF] text-white text-xs font-bold px-3 py-1 rounded-full">
                    Featured Project
                  </div>
                </div>
                <CardContent className="p-8 md:p-12 flex flex-col justify-center">
                  <div className="flex items-center gap-3 mb-4 text-sm text-gray-500">
                    <span className="bg-[#003366]/10 text-[#003366] px-3 py-1 rounded-full font-medium">{featured.category}</span>
                    <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{formatDate(featured.created_at)}</span>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-[#003366] mb-4 group-hover:text-[#00AEEF] transition-colors">
                    {featured.title}
                  </h2>
                  <p className="text-gray-600 mb-6 leading-relaxed">{featured.excerpt}</p>
                  <div className="flex items-center text-[#00AEEF] font-semibold group-hover:gap-3 gap-2 transition-all">
                    Read Full Article <ArrowRight className="w-4 h-4" />
                  </div>
                </CardContent>
              </div>
            </Card>
          </Link>
        )}

        {/* Post Grid */}
        {regular.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="blog-posts-grid">
            {regular.map(post => (
              <Link key={post.slug} to={`/blog/${post.slug}`} className="group" data-testid={`blog-card-${post.slug}`}>
                <Card className="overflow-hidden border-0 shadow-md hover:shadow-xl transition-all duration-300 h-full flex flex-col">
                  <div className="relative h-52 overflow-hidden">
                    <LazyImage
                      src={post.cover_image}
                      alt={post.title}
                      className="w-full h-full group-hover:scale-105 transition-transform duration-500"
                      zoomable={false}
                    />
                    <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm text-[#003366] text-xs font-semibold px-3 py-1 rounded-full">
                      {post.category}
                    </div>
                  </div>
                  <CardContent className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-3 text-xs text-gray-400 mb-3">
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(post.created_at)}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{readTime(post.content)}</span>
                    </div>
                    <h3 className="text-lg font-bold text-[#003366] mb-2 group-hover:text-[#00AEEF] transition-colors line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-sm text-gray-500 mb-4 line-clamp-3 flex-1">{post.excerpt}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {post.tags.slice(0, 3).map(tag => (
                        <span key={tag} className="flex items-center gap-0.5 text-xs text-gray-400 bg-gray-50 px-2 py-0.5 rounded">
                          <Tag className="w-2.5 h-2.5" />{tag}
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}

        {filtered.length === 0 && (
          <div className="text-center py-16">
            <p className="text-gray-400 text-lg">No blog posts found in this category.</p>
          </div>
        )}

        {/* Newsletter Capture */}
        <NewsletterCapture source="blog-listing" />

        {/* CTA */}
        <div className="mt-16 text-center bg-gradient-to-r from-[#003366] to-[#001a33] rounded-2xl p-10 text-white">
          <h2 className="text-2xl md:text-3xl font-bold mb-3">Ready to Build Your Dream Pool?</h2>
          <p className="text-[#00AEEF]/80 mb-6 max-w-xl mx-auto">From concept to completion, Aquarist Pools delivers premium pool construction pan India.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild className="bg-[#00AEEF] hover:bg-[#0098d4] text-white px-8 py-6 text-base font-semibold">
              <Link to="/booking">Get a Free Quote</Link>
            </Button>
            <Button asChild variant="outline" className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-6 text-base font-semibold">
              <Link to="/contact">Contact Us</Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Blog;
